/**
 * PlayerOverlay — custom playback HUD for Movies / Series.
 *
 * v1.3.94 — Replaces the browser's default `<video controls>` HUD with a
 * bespoke overlay:
 *   • Top:    scrubber + elapsed / total time
 *   • Middle: poster + title + "RATING x GENRE x DATE" metadata line
 *   • Bottom: rewind 10s · play/pause · fwd 10s · aspect · CC · settings · audio
 *
 * Behaviour:
 *   • Auto-hides after 3s of no interaction.
 *   • Any mouse-move, touch, or D-pad key press wakes it.
 *   • Attaches directly to a `<video>` element via ref; owns none of the
 *     underlying HLS / MediaSource logic.
 *
 * Live TV is intentionally excluded — LiveTvWatch has its own EPG-driven
 * overlay and no meaningful "duration" for the scrubber.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Rewind,
  FastForward,
  Play,
  Pause,
  LayoutGrid,
  Captions,
  SlidersHorizontal,
  Music,
} from "lucide-react";

// Formats seconds → HH:MM:SS (always three groups so the visual width stays stable
// even for a short movie — matches the mockup's "00:00:05 / 02:00:16" style).
function formatDuration(secs) {
  if (!Number.isFinite(secs) || secs < 0) return "00:00:00";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

// Aspect-ratio cycle so users can force a specific behaviour when the source
// is mislabeled. `contain` = letterbox (default), `cover` = fill+crop,
// `fill` = stretch. Applied as `object-fit` on the underlying <video>.
const ASPECT_MODES = ["contain", "cover", "fill"];
const ASPECT_LABELS = { contain: "Fit", cover: "Zoom", fill: "Stretch" };

export default function PlayerOverlay({
  videoRef,
  title,
  poster,
  rating,
  genre,
  releaseDate,
  onOpenSubtitles,
  onOpenAudioTracks,
  onOpenSettings,
}) {
  const [visible, setVisible] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [aspect, setAspect] = useState("contain");
  const hideTimer = useRef(null);
  const scrubbing = useRef(false);

  const scheduleHide = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      // Don't hide mid-scrub — user is actively holding the thumb.
      if (!scrubbing.current) setVisible(false);
    }, 3000);
  }, []);

  const wake = useCallback(() => {
    setVisible(true);
    scheduleHide();
  }, [scheduleHide]);

  // Wire up wake events: mouse movement, touches, and any key press
  // (crucial for TV D-pad remotes which fire keydown for UP/DOWN/OK).
  useEffect(() => {
    const onMove = () => wake();
    const onKey = () => wake();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchstart", onMove, { passive: true });
    window.addEventListener("keydown", onKey);
    scheduleHide();
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchstart", onMove);
      window.removeEventListener("keydown", onKey);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [wake, scheduleHide]);

  // Bind to the <video> element for play/pause + time updates + duration.
  useEffect(() => {
    const v = videoRef?.current;
    if (!v) return;
    const onTime = () => setCurrent(v.currentTime || 0);
    const onDur = () => setDuration(v.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onDur);
    v.addEventListener("durationchange", onDur);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    // Initial sync in case the events already fired before we bound.
    setCurrent(v.currentTime || 0);
    setDuration(v.duration || 0);
    setPlaying(!v.paused);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onDur);
      v.removeEventListener("durationchange", onDur);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [videoRef]);

  // Apply aspect-ratio mode by mutating `objectFit` on the <video>. Kept
  // inside an effect so it re-applies if the parent hot-swaps videoRef.
  useEffect(() => {
    const v = videoRef?.current;
    if (v && v.style) v.style.objectFit = aspect;
  }, [aspect, videoRef]);

  const togglePlay = () => {
    const v = videoRef?.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
    wake();
  };

  const seekRelative = (delta) => {
    const v = videoRef?.current;
    if (!v || !Number.isFinite(v.duration)) return;
    v.currentTime = Math.max(0, Math.min(v.duration, v.currentTime + delta));
    wake();
  };

  const cycleAspect = () => {
    setAspect((a) => ASPECT_MODES[(ASPECT_MODES.indexOf(a) + 1) % ASPECT_MODES.length]);
    wake();
  };

  const onScrub = (e) => {
    const v = videoRef?.current;
    if (!v || !Number.isFinite(v.duration)) return;
    const pct = Number(e.target.value);
    v.currentTime = (pct / 100) * v.duration;
    setCurrent(v.currentTime);
  };

  const progressPct = duration > 0 ? (current / duration) * 100 : 0;

  // Metadata footer line — only show pieces that exist. Kept compact so it
  // stays on one line even on narrow phones.
  const metaBits = [];
  if (rating) metaBits.push(`RATING: ${Number(rating).toFixed(3)}`);
  if (genre) metaBits.push(`GENRE: ${genre}`);
  if (releaseDate) metaBits.push(`DATE: ${releaseDate}`);
  const metaLine = metaBits.join("  ");

  return (
    <div
      data-testid="player-overlay"
      className={`absolute inset-x-0 bottom-0 z-30 transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      // A dark → transparent gradient anchors the overlay against bright
      // scenes without a hard black bar.
      style={{
        background:
          "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.65) 60%, rgba(0,0,0,0) 100%)",
      }}
      onMouseEnter={wake}
      onClick={wake}
    >
      {/* Row 1 — progress bar with times */}
      <div className="px-6 pt-6 pb-2 flex items-center gap-4 text-white text-sm font-mono">
        <span data-testid="player-time-current">{formatDuration(current)}</span>
        <input
          type="range"
          min={0}
          max={100}
          step={0.1}
          value={progressPct}
          onChange={onScrub}
          onMouseDown={() => (scrubbing.current = true)}
          onMouseUp={() => {
            scrubbing.current = false;
            scheduleHide();
          }}
          onTouchStart={() => (scrubbing.current = true)}
          onTouchEnd={() => {
            scrubbing.current = false;
            scheduleHide();
          }}
          data-testid="player-scrubber"
          className="flex-1 h-1.5 accent-orange-400 cursor-pointer"
          aria-label="Seek"
        />
        <span data-testid="player-time-total">{formatDuration(duration)}</span>
      </div>

      {/* Row 2 — poster + title + metadata */}
      {(title || poster || metaLine) && (
        <div className="px-6 py-4 flex items-end gap-4">
          {poster && (
            <img
              src={poster}
              alt=""
              className="w-16 h-24 sm:w-20 sm:h-28 rounded-md object-cover flex-shrink-0 shadow-lg"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              data-testid="player-poster"
            />
          )}
          <div className="min-w-0 flex-1">
            {title && (
              <div
                data-testid="player-title"
                className="text-white text-xl sm:text-2xl font-semibold truncate"
              >
                {title}
              </div>
            )}
            {metaLine && (
              <div
                data-testid="player-meta"
                className="text-slate-300 text-xs sm:text-sm mt-1 truncate"
              >
                {metaLine}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Row 3 — control buttons, right-aligned like the mockup */}
      <div className="px-6 pb-6 flex justify-end items-center gap-6 text-white">
        <button
          type="button"
          onClick={() => seekRelative(-10)}
          data-testid="player-btn-rewind"
          aria-label="Rewind 10 seconds"
          className="p-2 hover:text-orange-400 transition-colors"
        >
          <Rewind className="w-8 h-8" strokeWidth={1.5} />
        </button>

        <button
          type="button"
          onClick={togglePlay}
          data-testid="player-btn-playpause"
          aria-label={playing ? "Pause" : "Play"}
          className="p-2 hover:text-orange-400 transition-colors"
        >
          {playing ? (
            <Pause className="w-9 h-9" strokeWidth={1.5} />
          ) : (
            <Play className="w-9 h-9" strokeWidth={1.5} />
          )}
        </button>

        <button
          type="button"
          onClick={() => seekRelative(10)}
          data-testid="player-btn-forward"
          aria-label="Fast-forward 10 seconds"
          className="p-2 hover:text-orange-400 transition-colors"
        >
          <FastForward className="w-8 h-8" strokeWidth={1.5} />
        </button>

        <button
          type="button"
          onClick={cycleAspect}
          data-testid="player-btn-aspect"
          aria-label={`Aspect ratio: ${ASPECT_LABELS[aspect]}`}
          title={`Aspect: ${ASPECT_LABELS[aspect]} (click to cycle)`}
          className="p-2 hover:text-orange-400 transition-colors"
        >
          <LayoutGrid className="w-8 h-8" strokeWidth={1.5} />
        </button>

        <button
          type="button"
          onClick={onOpenSubtitles}
          data-testid="player-btn-subtitles"
          aria-label="Subtitles"
          className="p-2 hover:text-orange-400 transition-colors"
        >
          <Captions className="w-8 h-8" strokeWidth={1.5} />
        </button>

        <button
          type="button"
          onClick={onOpenSettings}
          data-testid="player-btn-settings"
          aria-label="Playback settings"
          className="p-2 hover:text-orange-400 transition-colors"
        >
          <SlidersHorizontal className="w-8 h-8" strokeWidth={1.5} />
        </button>

        <button
          type="button"
          onClick={onOpenAudioTracks}
          data-testid="player-btn-audio"
          aria-label="Audio tracks"
          className="p-2 hover:text-orange-400 transition-colors"
        >
          <Music className="w-8 h-8" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
